import { Guid } from 'src/application/store/model/guid';

import { Try } from '../../../../../types/Try';
import { PreviouslySelectedOptionsForWidgets } from '../../../../reducers/student/tracking/questionWidgetTrackingReducer';
import { SemicolonSerializer } from '../serializer/Serializer';
import {
  CourseCompletionStatus,
  IPersistenceTrackingClient,
  PersistenceTrackingData,
  TrackingDataVersion,
} from '../trackingClient';
import { TinCanStatementsFactory } from './TinCanStatementsFactory';
import { xApiWrapper } from './xApiWrapper';
import { TrackingIdentifier } from '@ael/core-api';
import { Map, Set } from 'immutable';

type milliseconds = number;

interface CourseData {
  name: string;
  guid: Guid;
}

interface xApiStateData {
  log: string;
  duration: milliseconds;
  apiVersion: number;
}

interface xApiCompletionState {
  timestamp: number;
}

const trackingDataStateId = 'trackingData';
const completionStateId = 'completionId';

export class TinCanTrackingClient implements IPersistenceTrackingClient {
  private readonly _statementFactory: TinCanStatementsFactory;
  private completionState: xApiCompletionState | null = null;
  private timeTaken: number;
  private timeLastUpdated: number;

  constructor(
    _courseData: CourseData,
    private readonly _xApi: xApiWrapper,
    xApiBaseUrl: string,
    currentDateNow: number = Date.now()
  ) {
    this._statementFactory = new TinCanStatementsFactory(_courseData.guid, _courseData.name, xApiBaseUrl);
    this.timeLastUpdated = currentDateNow;
  }

  name = 'TinCan';
  doesSupportLegacyData = false;
  async init(): Promise<boolean> {
    try {
      await this._xApi.sendStatement((actor) => this._statementFactory.createOpened(actor));
      this.completionState = await this._getCompletionStateFromLRS();
    } catch (ex) {
      console.log('Failed to initialize TinCan.');
      console.log(ex);
      return Promise.resolve(false);
    }
    return Promise.resolve(true);
  }

  async getTrackingData(): Promise<PersistenceTrackingData> {
    const storedStateResult = await this._getTrackingDataFromLRS();
    if (storedStateResult.success) {
      this.timeTaken = storedStateResult.value.duration;
      return SemicolonSerializer.deserializeData(storedStateResult.value.log);
    }

    this.timeTaken = 0;
    return {
      finishedItems: Set<TrackingIdentifier>(),
      widgetTrackingData: Map<TrackingIdentifier, PreviouslySelectedOptionsForWidgets>(),
      version: TrackingDataVersion.v2,
    };
  }

  afterSetup(): Promise<void> {
    return Promise.resolve(undefined);
  }

  async setTrackingData({ widgetTrackingData, finishedItems }: PersistenceTrackingData): Promise<void> {
    this.timeTaken = this._getTimeStudentSpentInCourse();
    const log = SemicolonSerializer.serializeData(finishedItems, widgetTrackingData);
    await this._storeTrackingDataToLRS(log, this.timeTaken);
    this.timeLastUpdated = Date.now();
  }

  async setCompletionStatus(status: CourseCompletionStatus) {
    if (status === CourseCompletionStatus.Completed && !this.completionState?.timestamp) {
      this.timeTaken = this._getTimeStudentSpentInCourse();
      const durationInIso = this._convertToIso(this.timeTaken);
      await this._xApi.sendStatement((actor) => this._statementFactory.createCompleted(actor, durationInIso));
      await this._storeCompletionStateToLRS(Date.now());
    }
  }

  async setQuizStatus() {
    return Promise.resolve();
  }

  private async _storeTrackingDataToLRS(log: string, duration: milliseconds): Promise<void> {
    const state: xApiStateData = {
      log,
      duration: duration,
      apiVersion: 1,
    };
    await this._xApi.storeState(trackingDataStateId, state);
  }

  private async _getTrackingDataFromLRS(): Promise<Try<xApiStateData>> {
    return await this._xApi.getStateById<xApiStateData>(trackingDataStateId);
  }

  private async _storeCompletionStateToLRS(timestamp: number): Promise<void> {
    const state: xApiCompletionState = { timestamp };
    this.completionState = state;
    await this._xApi.storeState(completionStateId, state);
  }

  private async _getCompletionStateFromLRS(): Promise<xApiCompletionState | null> {
    const result = await this._xApi.getStateById<xApiCompletionState>(completionStateId);
    return result.success ? result.value : null;
  }

  private _getTimeStudentSpentInCourse(): number {
    return Date.now() - this.timeLastUpdated + this.timeTaken;
  }

  private _convertToIso(duration: milliseconds) {
    let seconds = Math.round(duration / 1000);
    if (seconds > 60) {
      if (seconds > 3600) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        seconds = (seconds % 3600) % 60;
        return `PT${hours}H${minutes}M${seconds}S`;
      } else {
        const minutes = Math.floor(seconds / 60);
        seconds %= 60;
        return `PT${minutes}M${seconds}S`;
      }
    } else {
      return `PT${seconds}S`;
    }
  }
}
